import React, { useEffect, useState } from 'react';
import { Box, Chip, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { api } from '../../../services/apiClient';
import { TableSurface, headerCellSx, bodyRowSx, StatusDot, TableEmptyState } from '../../common/pageKit';
import { formatINR, formatNumber } from '../../../dashboard/primitives';
import { formatDateIN } from '../../../pages/sales/salesReportsHelpers';

/**
 * GST Summary and Invoice Ageing — the two reports from the previous Sales
 * Reports page that erp-api/controllers/sales.js#getReports still serves and
 * nothing above replaces. Kept, restyled onto the shared page kit; the
 * Sales Summary / Customer-wise / Product-wise tabs of the old page are
 * dropped because /sales-analytics/* now covers that ground with real
 * null-vs-zero discipline and comparison periods, which /sales/reports never had.
 */

interface GstSummaryRow {
  total_cgst: number | string;
  total_sgst: number | string;
  total_igst: number | string;
  total_gst: number | string;
  taxable_amount: number | string;
  invoice_count: number | string;
}
interface GstByRateRow {
  gst_rate: number | string;
  taxable_amount: number | string;
  cgst: number | string;
  sgst: number | string;
  igst: number | string;
  total_tax: number | string;
}
interface GstB2bB2cRow {
  type: string;
  invoice_count: number | string;
  total_amount: number | string;
  total_gst: number | string;
}
interface GstMonthlyRow {
  month: string;
  month_label: string;
  intra_state_gst: number | string;
  inter_state_gst: number | string;
  total_gst: number | string;
}
interface GstReport {
  summary: GstSummaryRow;
  byRate: GstByRateRow[];
  b2bB2c: GstB2bB2cRow[];
  monthly: GstMonthlyRow[];
}

interface AgingInvoiceRow {
  id: number;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  customer_name: string;
  total_amount: number | string;
  paid_amount: number | string;
  outstanding: number | string;
  days_overdue: number | string;
  aging_bucket: string;
}
interface AgingSummaryRow {
  bucket: string;
  invoice_count: number | string;
  outstanding_amount: number | string;
}
interface AgingReport {
  invoices: AgingInvoiceRow[];
  summary: AgingSummaryRow[];
  totals: { total_invoices: number | string; total_outstanding: number | string };
}

const n = (v: number | string): number => Number(v);

const bucketTone = (bucket: string): 'success' | 'warning' | 'error' =>
  bucket === 'Current' ? 'success' : bucket.includes('90') ? 'error' : 'warning';

interface ComplianceReportsSectionProps {
  from: string;
  to: string;
}

const ComplianceReportsSection: React.FC<ComplianceReportsSectionProps> = ({ from, to }) => {
  const [kind, setKind] = useState<'gst' | 'aging'>('gst');
  const [gst, setGst] = useState<GstReport | null>(null);
  const [aging, setAging] = useState<AgingReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<any>('/sales/reports', { params: { reportType: kind, fromDate: from, toDate: to } })
      .then((data) => {
        if (cancelled) return;
        if (kind === 'gst') setGst(data as GstReport);
        else setAging(data as AgingReport);
      })
      .catch((err: any) => !cancelled && setError(err.message || 'Failed to load report'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, from, to]);

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ fontWeight: 700 }}>Compliance Reports</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip label="GST Summary" size="small" clickable color={kind === 'gst' ? 'primary' : 'default'} variant={kind === 'gst' ? 'filled' : 'outlined'} onClick={() => setKind('gst')} />
          <Chip label="Invoice Ageing" size="small" clickable color={kind === 'aging' ? 'primary' : 'default'} variant={kind === 'aging' ? 'filled' : 'outlined'} onClick={() => setKind('aging')} />
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <TableEmptyState title="Could not load this report" hint={error} />
      ) : kind === 'gst' && gst ? (
        <GstView data={gst} />
      ) : kind === 'aging' && aging ? (
        <AgingView data={aging} />
      ) : null}
    </Box>
  );
};

const GstView: React.FC<{ data: GstReport }> = ({ data }) => (
  <Box>
    <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
      <Stat label="CGST" value={formatINR(n(data.summary.total_cgst))} />
      <Stat label="SGST" value={formatINR(n(data.summary.total_sgst))} />
      <Stat label="IGST" value={formatINR(n(data.summary.total_igst))} />
      <Stat label="Total GST" value={formatINR(n(data.summary.total_gst))} />
      <Stat label="Taxable Amount" value={formatINR(n(data.summary.taxable_amount))} />
      <Stat label="Invoices" value={formatNumber(n(data.summary.invoice_count))} />
    </Box>

    <TableSurface>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 640 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>GST Rate</TableCell>
              <TableCell sx={headerCellSx} align="right">Taxable</TableCell>
              <TableCell sx={headerCellSx} align="right">CGST</TableCell>
              <TableCell sx={headerCellSx} align="right">SGST</TableCell>
              <TableCell sx={headerCellSx} align="right">IGST</TableCell>
              <TableCell sx={headerCellSx} align="right">Total Tax</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.byRate.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ border: 0 }}>
                  <TableEmptyState title="No GST activity in this period" />
                </TableCell>
              </TableRow>
            ) : (
              data.byRate.map((row, i) => (
                <TableRow key={i} sx={bodyRowSx}>
                  <TableCell>{n(row.gst_rate)}%</TableCell>
                  <TableCell sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatINR(n(row.taxable_amount))}</TableCell>
                  <TableCell sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatINR(n(row.cgst))}</TableCell>
                  <TableCell sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatINR(n(row.sgst))}</TableCell>
                  <TableCell sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatINR(n(row.igst))}</TableCell>
                  <TableCell sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatINR(n(row.total_tax))}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>
    </TableSurface>
  </Box>
);

const AgingView: React.FC<{ data: AgingReport }> = ({ data }) => (
  <Box>
    <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
      {data.summary.map((b) => (
        <Stat key={b.bucket} label={b.bucket} value={`${formatINR(n(b.outstanding_amount), true)} · ${formatNumber(n(b.invoice_count))} inv`} />
      ))}
      <Stat label="Total Outstanding" value={formatINR(n(data.totals.total_outstanding))} emphasize />
    </Box>

    <TableSurface>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Invoice</TableCell>
              <TableCell sx={headerCellSx}>Customer</TableCell>
              <TableCell sx={headerCellSx}>Due Date</TableCell>
              <TableCell sx={headerCellSx} align="right">Total</TableCell>
              <TableCell sx={headerCellSx} align="right">Outstanding</TableCell>
              <TableCell sx={headerCellSx}>Ageing</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ border: 0 }}>
                  <TableEmptyState title="Nothing outstanding" hint="Every invoice is fully paid." />
                </TableCell>
              </TableRow>
            ) : (
              data.invoices.map((inv) => (
                <TableRow key={inv.id} sx={bodyRowSx}>
                  <TableCell>{inv.invoice_number}</TableCell>
                  <TableCell>{inv.customer_name}</TableCell>
                  <TableCell>{formatDateIN(inv.due_date)}</TableCell>
                  <TableCell sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatINR(n(inv.total_amount))}</TableCell>
                  <TableCell sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatINR(n(inv.outstanding))}</TableCell>
                  <TableCell>
                    <StatusDot label={inv.aging_bucket} tone={bucketTone(inv.aging_bucket)} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>
    </TableSurface>
  </Box>
);

const Stat: React.FC<{ label: string; value: string; emphasize?: boolean }> = ({ label, value, emphasize }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography sx={{ fontWeight: emphasize ? 800 : 700, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
  </Box>
);

export default ComplianceReportsSection;
